import { useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from './Icon'
import { useLeaveSheet } from './sheetMotion'

export interface ReminderSnoozeOption {
  id: 'fifteen-minutes' | 'one-hour' | 'tomorrow-morning'
  label: string
  detail: string
  durationMs: number
}

export function reminderSnoozeOptions(now = Date.now()): ReminderSnoozeOption[] {
  const tomorrowMorning = new Date(now)
  tomorrowMorning.setDate(tomorrowMorning.getDate() + 1)
  tomorrowMorning.setHours(8, 0, 0, 0)
  return [
    { id: 'fifteen-minutes', label: '15 min', detail: 'A quick pause', durationMs: 15 * 60 * 1_000 },
    { id: 'one-hour', label: '1 hour', detail: 'Later today', durationMs: 60 * 60 * 1_000 },
    {
      id: 'tomorrow-morning',
      label: 'Tomorrow 8 AM',
      detail: 'Start fresh tomorrow',
      durationMs: Math.max(60_000, tomorrowMorning.getTime() - now),
    },
  ]
}

interface NativeReminderSnoozeDialogProps {
  title: string
  onClose: () => void
  onSnooze: (durationMs: number) => Promise<boolean>
}

export function NativeReminderSnoozeDialog({
  title,
  onClose,
  onSnooze,
}: NativeReminderSnoozeDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const [saving, setSaving] = useState(false)
  const leave = useLeaveSheet(dialogRef, onClose)

  useLayoutEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal()
    else dialog.setAttribute('open', '')
    return () => {
      if (!dialog.open) return
      if (typeof dialog.close === 'function') dialog.close()
      else dialog.removeAttribute('open')
    }
  }, [])

  const choose = async (durationMs: number) => {
    if (saving) return
    setSaving(true)
    const saved = await onSnooze(durationMs)
    if (saved) leave()
    else setSaving(false)
  }

  return createPortal(
    <dialog
      ref={dialogRef}
      className="modal modal-bottom sm:modal-middle studio-sheet native-reminder-snooze-modal"
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault()
        if (!saving) leave()
      }}
    >
      <section className="modal-box sheet-panel native-reminder-snooze" aria-describedby={`${titleId}-description`}>
        <header className="sheet-header">
          <h2 id={titleId} className="sheet-title"><Icon name="clock" />Snooze reminder</h2>
          <button type="button" className="btn btn-ghost btn-square sheet-close" aria-label="Close snooze choices" onClick={leave} disabled={saving}>
            <Icon name="close" />
          </button>
        </header>
        <div className="sheet-body">
          <p id={`${titleId}-description`} className="sheet-note"><strong>{title}</strong><br />Choose when you want to be reminded again.</p>
          <div className="native-reminder-snooze__choices" aria-label="Snooze length">
            {reminderSnoozeOptions().map((option) => (
              <button
                key={option.id}
                type="button"
                className="btn native-reminder-snooze__choice"
                onClick={() => void choose(option.durationMs)}
                disabled={saving}
              >
                <span>{option.label}</span>
                <small>{option.detail}</small>
              </button>
            ))}
          </div>
        </div>
        <div className="sheet-footer">
          <button type="button" className="btn btn-ghost" onClick={leave} disabled={saving}>Keep reminder</button>
        </div>
      </section>
      <form method="dialog" className="modal-backdrop" onSubmit={(event) => {
        event.preventDefault()
        if (!saving) leave()
      }}>
        <button aria-label="Close snooze choices" />
      </form>
    </dialog>,
    document.body,
  )
}
